
import { gtcHandleize, gtcGetCookie, gtcSetCookie } from "gtcUtils";
import { gtcModal } from "gtcModal";

const getLanguageFromCountryISO = (countryISO) => {
  const countryLanguageMap = {
    // Europe
    'AD': 'ca', // Andorra -> Catalan
    'AL': 'sq', // Albania -> Albanian
    'AT': 'de', // Austria -> German
    'BA': 'bs', // Bosnia and Herzegovina -> Bosnian
    'BE': 'nl', // Belgium -> Dutch (also French)
    'BG': 'bg', // Bulgaria -> Bulgarian
    'BY': 'be', // Belarus -> Belarusian
    'CH': 'de', // Switzerland -> German (also French, Italian)
    'CY': 'el', // Cyprus -> Greek
    'CZ': 'cs', // Czech Republic -> Czech
    'DE': 'de', // Germany -> German
    'DK': 'da', // Denmark -> Danish
    'EE': 'et', // Estonia -> Estonian
    'ES': 'es', // Spain -> Spanish
    'FI': 'fi', // Finland -> Finnish
    'FR': 'fr', // France -> French
    'GB': 'en', // United Kingdom -> English
    'GR': 'el', // Greece -> Greek
    'HR': 'hr', // Croatia -> Croatian
    'HU': 'hu', // Hungary -> Hungarian
    'IE': 'en', // Ireland -> English
    'IS': 'is', // Iceland -> Icelandic
    'IT': 'it', // Italy -> Italian
    'LI': 'de', // Liechtenstein -> German
    'LT': 'lt', // Lithuania -> Lithuanian
    'LU': 'lb', // Luxembourg -> Luxembourgish
    'LV': 'lv', // Latvia -> Latvian
    'MC': 'fr', // Monaco -> French
    'MD': 'ro', // Moldova -> Romanian
    'ME': 'sr', // Montenegro -> Serbian
    'MK': 'mk', // North Macedonia -> Macedonian
    'MT': 'mt', // Malta -> Maltese
    'NL': 'nl', // Netherlands -> Dutch
    'NO': 'no', // Norway -> Norwegian
    'PL': 'pl', // Poland -> Polish
    'PT': 'pt', // Portugal -> Portuguese
    'RO': 'ro', // Romania -> Romanian
    'RS': 'sr', // Serbia -> Serbian
    'RU': 'ru', // Russia -> Russian
    'SE': 'sv', // Sweden -> Swedish
    'SI': 'sl', // Slovenia -> Slovenian
    'SK': 'sk', // Slovakia -> Slovak
    'SM': 'it', // San Marino -> Italian
    'UA': 'uk', // Ukraine -> Ukrainian
    'VA': 'it', // Vatican City -> Italian
    'XK': 'sq', // Kosovo -> Albanian

    // Other major countries
    'US': 'en', // United States -> English
    'CA': 'en', // Canada -> English
    'JP': 'ja', // Japan -> Japanese
    'CN': 'zh', // China -> Chinese
    'BR': 'pt', // Brazil -> Portuguese
    'MX': 'es', // Mexico -> Spanish
    'AR': 'es', // Argentina -> Spanish
    'AU': 'en', // Australia -> English
    'IN': 'hi', // India -> Hindi
    'ZA': 'en', // South Africa -> English
    'KR': 'ko', // South Korea -> Korean
    'TH': 'th', // Thailand -> Thai
    'VN': 'vi', // Vietnam -> Vietnamese
    'TR': 'tr', // Turkey -> Turkish
    'SA': 'ar', // Saudi Arabia -> Arabic
    'AE': 'ar', // United Arab Emirates -> Arabic
    'EG': 'ar', // Egypt -> Arabic
    'IL': 'he', // Israel -> Hebrew
    'IR': 'fa', // Iran -> Persian
    'PK': 'ur', // Pakistan -> Urdu
    'BD': 'bn', // Bangladesh -> Bengali
    'ID': 'id', // Indonesia -> Indonesian
    'MY': 'ms', // Malaysia -> Malay
    'SG': 'en', // Singapore -> English
    'PH': 'en', // Philippines -> English
    'NG': 'en', // Nigeria -> English
    'KE': 'sw', // Kenya -> Swahili
    'MA': 'ar', // Morocco -> Arabic
    'DZ': 'ar', // Algeria -> Arabic
    'TN': 'ar', // Tunisia -> Arabic
    'CL': 'es', // Chile -> Spanish
    'PE': 'es', // Peru -> Spanish
    'CO': 'es', // Colombia -> Spanish
    'VE': 'es', // Venezuela -> Spanish
    'EC': 'es', // Ecuador -> Spanish
    'UY': 'es', // Uruguay -> Spanish
    'PY': 'es', // Paraguay -> Spanish
    'BO': 'es', // Bolivia -> Spanish
  };

  return countryLanguageMap[countryISO.toUpperCase()] || null;
}


class GTCGeolocator extends gtcModal {

  #shouldOpen = false;

  constructor() {
    super();

    this.formSubmit = this.formSubmit.bind(this);
    this.changeCountry = this.changeCountry.bind(this);
    this.changeLanguage = this.changeLanguage.bind(this);

  }

  connectedCallback() {
    super.connectedCallback();

    this.dom = {
      flagImg: this.querySelector("[data-geolocator-flag]"),
      countryList: this.querySelector("[data-country-list]") ?? null,
      languageList: this.querySelector("[data-language-list]") ?? null,
      form: this.querySelector("form"),
      title: this.querySelector("[data-geolocator-title]"),
      message: this.querySelector("[data-geolocator-message]"),
      languageMessage: this.querySelector("[data-geolocator-language-message]"),
      submit: this.querySelector("[data-geolocator-submit]"),
      inputCountryCode: this.querySelector("input[name='country_code']") ?? null,
      inputLanguageCode: this.querySelector("input[name='language_code']") ?? null,
      inputReturnTo: this.querySelector("input[name='return_to']") ?? null,
    };

    this.settings = {
      ...this.settings,
      isMarket: this.getAttribute("is-market") === "true",
      lang: 'en',
      cookieName: "GTC_GEOLOCATOR",
      currentCountry: this.getAttribute("current-country") || "",
      currentLanguage: this.getAttribute("current-language") || "",
      currentPageType: this.getAttribute("current-page-type") || "",
      translate: this.getAttribute("translate"),
      defaultStrings: {
        title: this.querySelector("[data-geolocator-title]")?.getAttribute("data-geolocator-title"),
        message: this.querySelector("[data-geolocator-message]")?.getAttribute("data-geolocator-message"),
        languageMessage: this.querySelector("[data-geolocator-language-message]")?.getAttribute("data-geolocator-language-message"),
        submit: this.querySelector("[data-geolocator-submit]")?.getAttribute("data-geolocator-submit"),
      },
    };



    this.checkActiveChoice();

    if (gtcGetCookie(this.settings.cookieName) === null) {
      this.initGeoLocator();
    }

    this.addListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeListeners();
  }


  addListeners() {
    this.dom.countryList.addEventListener("change", this.changeCountry);
    this.dom.languageList.addEventListener("change", this.changeLanguage);
    this.dom.form.addEventListener("submit", this.formSubmit);

  }

  removeListeners() {
    this.dom.countryList.removeEventListener("change", this.changeCountry);
    this.dom.languageList.removeEventListener("change", this.changeLanguage);
    this.dom.form.removeEventListener("submit", this.formSubmit);
  }

  /**
 * Initializes the geolocator popup.
 * Fetches the user's country and updates the flag image accordingly.
 * Handles the country selection change event.
 * Updates the geolocator state based on the selected country.
 * If the selected country is the main country, it removes the active class and restores the body overflow.
 */
  async initGeoLocator() {
    const countryList = this.dom.countryList
    const countryOptions = Array.from(countryList.options).map(option => option.value);
    const country = await this.getCountry();

    let lang = navigator.language || navigator.userLanguage;

    // if geojs lang
    if (this.settings.translate === "geojs") {
      lang = getLanguageFromCountryISO(country) || country;
    }

    this.settings.lang = lang;

    if (this.settings.translate) {
      this.translate();
    }


    const indexOfCountry = countryOptions.indexOf(country);
    if (indexOfCountry !== -1) {
      this.updateText(countryList.options[indexOfCountry]);
      countryList.selectedIndex = indexOfCountry;
      countryList.dispatchEvent(new Event("change"));
    }

    if (countryList) {
      // If the user's country is not the same as the current country, open the geolocator.
      if (this.settings.currentCountry !== country) {
        // If the user's country is in the country options, select it and open the geolocator.
        if (countryOptions.includes(country)) {
          this.#shouldOpen = true;
        } else {
          const internationalIndex = Array.from(countryList.options).findIndex(option => option.getAttribute("data-international") !== null);
          if (internationalIndex !== -1) {
            this.updateFlag(countryList.options[internationalIndex].value);
            countryList.selectedIndex = internationalIndex;
            this.#shouldOpen = true;
            this.updateText(countryList.options[internationalIndex]);
          }
        }
      }

      if (this.#shouldOpen) {
        this.open();
      }
    }
  }

  /**
   * Fetches the country based on the user's IP address using the GeoJS API.
   * Returns the country name in a handleized format.
   * @returns {Promise<string>} The handleized country name.
   */
  async getCountry() {
    const response = await fetch("https://get.geojs.io/v1/ip/country.json");
    const body = await response.json();
    return gtcHandleize(body.country);
  }

  /**
   * Updates the flag image based on the selected country.
   * @param {string} countryCode - The selected country.
   */
  updateFlag(countryCode) {
    const flagImgSrc = this.dom.flagImg.getAttribute("src");
    const flagImgSrcSplit = flagImgSrc.split("/");
    flagImgSrcSplit[flagImgSrcSplit.length - 1] = `${ countryCode }.svg`;

    this.dom.flagImg.setAttribute("src", flagImgSrcSplit.join("/"));
  }
  updateText(matchingOption) {
    if (this.settings.translate) {

      let title = this.settings.defaultStrings.title;
      let message = this.settings.defaultStrings.message;
      let languageMessage = this.settings.defaultStrings.languageMessage;
      let submit = this.settings.defaultStrings.submit;
      let currency = this.settings.defaultStrings.currency;
      let location = this.settings.defaultStrings.location;

      if (matchingOption) {
        title = matchingOption.hasAttribute("data-title") ? matchingOption.getAttribute("data-title") : null || this.settings.defaultStrings.title;
        currency = matchingOption.hasAttribute("data-currency") ? matchingOption.getAttribute("data-currency") : null || this.settings.defaultStrings.currency;
        message = matchingOption.hasAttribute("data-message") ? matchingOption.getAttribute("data-message") : null || this.settings.defaultStrings.message;
        languageMessage = matchingOption.hasAttribute("data-language-message") ? matchingOption.getAttribute("data-language-message") : null || this.settings.defaultStrings.languageMessage;
        submit = matchingOption.hasAttribute("data-submit") ? matchingOption.getAttribute("data-submit") : null || this.settings.defaultStrings.submit;
        location = matchingOption.hasAttribute("data-location") ? matchingOption.getAttribute("data-location") : null || this.settings.defaultStrings.location;
      }


      if (title) {
        this.dom.title.textContent = title;
      }

      if (message) {
        const messageWithVariables = message.replace(/\[currency\]/g, currency).replace(/\[location\]/g, location);
        this.dom.message.innerHTML = messageWithVariables;
      }

      if (languageMessage) {
        this.dom.languageMessage.textContent = languageMessage;
      }

      if (submit) {
        this.dom.submit.textContent = submit;
      }
    }

  }

  async checkActiveChoice() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("active_choice") === "true") {
      searchParams.delete("active_choice");
      window.history.pushState(null, '', `${ window.location.pathname }?${ searchParams.toString() }`);
      gtcSetCookie(this.settings.cookieName, "true", 30);
    }
  }

  getUrl(url) {
    // if absolute url
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    } else {
      // if relative url
      return `${ window.location.origin }${ url }`;
    }
  }

  async formSubmit(e) {
    e.preventDefault();


    const selectedCountryOption = this.dom.countryList.value;
    const selectedlanguageOption = this.settings.isMarket ? this.dom.languageList.value : null;

    if (this.settings.isMarket ? selectedCountryOption === this.settings.currentCountry && selectedlanguageOption.value === this.settings.currentLanguage : selectedCountryOption === this.settings.currentCountry) {
      this.close();
    } else {

      this.addSpinner();
      if (this.settings.isMarket) {

        await new Promise(resolve => {

          const url = this.dom.inputReturnTo.value;
          const [pathname, search = ''] = url.split('?');
          const searchParams = new URLSearchParams(search);
          searchParams.set("active_choice", "true");  // Note: "true" as string, not boolean
          this.dom.inputReturnTo.value = `${ pathname }?${ searchParams.toString() }`;
          resolve();
        });

        this.dom.form.submit();

      } else {
        if (selectedOption.getAttribute("data-url") !== null) {

          const countryUrl = selectedOption.getAttribute("data-url");
          const newLocation = new URL(this.getUrl(countryUrl));

          if (this.settings.currentPageType == "product" || this.settings.currentPageType == "collection") {
            newLocation.pathname = window.location.pathname.slice(1);

            let response;
            try {
              response = await fetch(`${ newLocation }.json`);
            } catch (error) {
              console.warn('Fetch failed:', error);
            }

            if (response?.status == 200) {
              return window.location.href = newLocation
            }
          }

          gtcSetCookie(this.settings.cookieName, "true", 30);

          window.location.href = newLocation;
        } else {
          alert("No URL specified for this country.");
          return this.close();
        }
      }

      setTimeout(() => {
        this.removeSpinner();
        this.close();
      }, timeout = 5000);
    }
  }

  translate() {

    const regionNamesByBrowser = new Intl.DisplayNames([this.settings.lang, 'en'], { type: 'region' });

    for (let i = 0; i < this.dom.countryList.options.length; i++) {
      try {
        const countryCode = this.dom.countryList.options[i].value.toUpperCase();
        const translatedName = regionNamesByBrowser.of(countryCode);
        if (translatedName) {
          const currency = this.dom.countryList.options[i].text.split('-')[1];
          this.dom.countryList.options[i].text = `${ translatedName } - ${ currency }`;
        }
      } catch (error) {
        console.warn(`Failed to translate country code: ${ this.dom.countryList.options[i].value }`, error);
      }
    }
  }

  // Close the geolocator and set the cookie
  close() {
    super.close();
    gtcSetCookie(this.settings.cookieName, "true", 30);
  }

  changeCountry({ countryList = this.dom.countryList }) {
    const selectedOption = countryList.options[countryList.selectedIndex];
    const selectedCountryCode = selectedOption.value;
    const languageObject = selectedOption.hasAttribute("data-languages") ? selectedOption.getAttribute("data-languages") : "";

    if (this.dom.inputCountryCode) {
      this.dom.inputCountryCode.value = selectedCountryCode;
    }

    this.updateFlag(selectedCountryCode);
    this.updateLanguage(languageObject);
  }

  changeLanguage() {
    if (this.dom.inputLanguageCode) {
      this.dom.inputLanguageCode.value = this.dom.languageList.value
    }
  }

  updateLanguage(languageObject) {

    if (this.dom.languageList) {
      this.dom.languageList.innerHTML = "";

      if (languageObject) {

        const languages = JSON.parse(languageObject);
        languages.forEach(language => {

          if (!language.shop_locale || !language.shop_locale.locale) {
            console.warn("Invalid language object:", language);
            return;
          }

          if (!language.shop_locale.enabled || !language.shop_locale.published) {
            return;
          }

          const locale = language.shop_locale.locale;
          const option = document.createElement("option");
          option.value = locale;

          const regionNamesByBrowser = new Intl.DisplayNames([this.settings.lang, 'en'], { type: 'language' });
          const translatedName = regionNamesByBrowser.of(locale);
          option.textContent = translatedName;

          if (language.shop_locale.primary) {
            option.selected = true;
          }
          this.dom.languageList.appendChild(option);
        });
      }
    }

    this.dom.languageList.dispatchEvent(new Event("change"));
  }



  addSpinner() {
    if (this.dom.submit) {
      this.dom.submit.classList.add("gtc-geolocator-load-spinner");
      this.dom.submit.disabled = true;
    }
  }

  removeSpinner() {
    if (this.dom.submit) {
      this.dom.submit.classList.remove("gtc-geolocator-load-spinner");
      this.dom.submit.disabled = false;
    }
  }

}

if (!customElements.get("gtc-geolocator")) {
  customElements.define("gtc-geolocator", GTCGeolocator);
}